import { useState, useEffect, useCallback, useMemo } from "react";
import { App } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  getEmployeeUnitsOptions,
  getUnitsOptions,
  getUnitsTree,
} from "@/api/units";
import { globalSearch } from "@/api/search";
import { getFile } from "@/api/files";

import {
  FILTER_NAMES,
  ALL_SECTION_VALUES,
  INITIAL_TABLE_PARAMS,
  getInitialFilters,
  getSelectedSectionValues,
  areAllSectionsSelected,
} from "./constants";
import routes from "@/routes";

export const useSearchData = ({ isAdmins }) => {
  const { message } = App.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Опции для фильтров:
  const [optionsPending, setOptionsPending] = useState(true);
  const [units, setUnits] = useState([]); // units - список РГ для селекта,
  const [unitsTree, setUnitsTree] = useState([]); // unitsTree - древовидные РГ для TreeSelect.

  // Данные поиска, полученные с сервера.
  // null — поиск ещё не выполнялся (скрываем счётчик).
  // [] — поиск выполнен, результатов нет.
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(0);
  const [pending, setPending] = useState(false);
  const [isFiltersModalShow, setIsFiltersModalShow] = useState(false);
  const [tempFilters, setTempFilters] = useState(getInitialFilters());
  const [tableParams, setTableParams] = useState(INITIAL_TABLE_PARAMS);

  // Важное разделение состояний:
  // searchInput — текущее значение в инпуте (пользователь печатает),
  // tableParams.filters.search — примененный фильтр (по Enter/кнопке).
  // Это позволяет не делать запрос на каждый символ.
  const [searchInput, setSearchInput] = useState("");

  const selectedSections = getSelectedSectionValues(tableParams.filters);
  const tempSelectedSections = getSelectedSectionValues(tempFilters);
  const isAllSectionsSelected = areAllSectionsSelected(selectedSections);
  const isAllTempSectionsSelected =
    areAllSectionsSelected(tempSelectedSections);

  //Модальное окно информации о файле
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // забираем фильтры из URL
  const serializeParamsToUrl = useCallback((params) => {
    const { filters, pagination, sort } = params;

    const next = new URLSearchParams();

    if (filters.search) next.set("search", filters.search);

    if (pagination?.current && pagination.current !== 1) {
      next.set("page", pagination.current);
    }

    if (pagination?.pageSize) {
      next.set("perpage", pagination.pageSize);
    }

    if (sort) next.set("sort", sort);

    if (filters.search_entity_types?.length) {
      next.set("search_entity_types", filters.search_entity_types.join(","));
    }

    if (filters.type_filter && filters.type_filter !== "all") {
      next.set("type_filter", filters.type_filter);
    }

    if (filters.unit_uuid) {
      next.set("unit_uuid", filters.unit_uuid);
    }

    if (filters.include_deleted) {
      next.set("include_deleted", "true");
    }

    if (Array.isArray(filters.units_uuids) && filters.units_uuids.length) {
      const prepared = filters.units_uuids
        .map((item) => (typeof item === "object" ? item.value : item))
        .filter(Boolean);

      if (prepared.length) {
        next.set("units_uuids", prepared.join(","));
      }
    }

    return next;
  }, []);

  const parseParamsFromUrl = useCallback(() => {
    const params = Object.fromEntries(searchParams.entries());

    return {
      pagination: {
        ...INITIAL_TABLE_PARAMS.pagination,
        current: Number(params.page) || 1,
        pageSize:
          Number(params.perpage) || INITIAL_TABLE_PARAMS.pagination.pageSize,
      },
      sort: params.sort || null,
      filters: {
        ...getInitialFilters(),

        search: params.search?.trim() || null,

        search_entity_types: params.search_entity_types
          ? params.search_entity_types.split(",")
          : ALL_SECTION_VALUES,

        type_filter: params.type_filter || "all",

        include_deleted: params.include_deleted === "true",

        unit_uuid: params.unit_uuid || null,

        units_uuids: params.units_uuids ? params.units_uuids.split(",") : [],
      },
    };
  }, [searchParams]);

  // Преобразуем дерево РГ в плоский массив для логики access_level.
  // Нужен, чтобы быстро вычислять набор доступных units_uuids
  // по признаку readOnly.
  const getFlatUnits = useMemo(() => {
    const flat = [];

    function traverse(nodes) {
      nodes.forEach((node) => {
        flat.push(node);
        if (Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children);
        }
      });
    }

    if (!unitsTree) return [];

    traverse(unitsTree);
    return flat;
  }, [unitsTree]);

  const getUnitsByAccessLevel = useCallback(
    (accessLevel) => {
      if (accessLevel === "all") return [];

      return getFlatUnits
        .filter(({ readOnly }) =>
          accessLevel === "root" ? !readOnly : readOnly,
        )
        .map(({ value }) => value)
        .filter(Boolean);
    },
    [getFlatUnits],
  );

  const buildFilterPatch = useCallback(
    (name, val) => {
      const patch = {
        [name]: name === FILTER_NAMES.SEARCH ? val?.trim() || null : val,
      };

      if (name === FILTER_NAMES.UNITS_UUIDS) {
        patch.type_filter = "all";
      }

      if (name === FILTER_NAMES.ACCESS_LEVEL) {
        patch.units_uuids = getUnitsByAccessLevel(val);
      }

      return patch;
    },
    [getUnitsByAccessLevel],
  );

  // Готовим payload для globalSearch
  const buildSearchParams = useCallback((localTableParams) => {
    const { filters, sort, pagination } = localTableParams;

    const preparedSearch = filters.search?.trim();
    const preparedUnitsUuids = Array.isArray(filters.units_uuids)
      ? filters.units_uuids
          .map((item) => (typeof item === "object" ? item?.value : item))
          .filter(Boolean)
      : [];

    return {
      ...(pagination?.current && { page: pagination.current }),
      ...(pagination?.pageSize && { perpage: pagination.pageSize }),
      ...(preparedSearch && { search: preparedSearch }),
      ...(Array.isArray(filters.search_entity_types) && {
        search_entity_types: filters.search_entity_types,
      }),
      ...(filters.unit_uuid && { unit_uuid: filters.unit_uuid }),
      ...(preparedUnitsUuids.length > 0 && { units_uuids: preparedUnitsUuids }),
      ...(filters.type_filter && { type_filter: filters.type_filter }),
      include_deleted: !!filters.include_deleted,
      ...(sort && { sort }),
    };
  }, []);

  // Универсальная загрузка данных страницы поиска
  const fetchData = useCallback(
    async (localTableParams) => {
      if (!localTableParams.filters.search) return;

      setPending(true);

      try {
        const payload = buildSearchParams(localTableParams);
        const { total, data } = await globalSearch(payload, isAdmins);

        setData(data);
        setTotal(total);
      } catch (error) {
        setData([]);
        setTotal(0);
        message.open({
          type: "error",
          content: error?.message || "Ошибка загрузки данных",
          duration: 10,
        });
      } finally {
        setPending(false);
      }
    },

    [buildSearchParams, isAdmins, message],
  );

  // Инициализация фильтров при загрузке страницы
  const fetchOptions = useCallback(async () => {
    setOptionsPending(true);

    try {
      const [{ units: unitsRes }, treeRes] = await Promise.all([
        isAdmins ? getUnitsOptions() : getEmployeeUnitsOptions(),
        getUnitsTree({ from_unit: true }),
      ]);

      setUnits(unitsRes);
      setUnitsTree(treeRes);
    } catch (error) {
      message.open({
        type: "error",
        content: error?.message || "Ошибка загрузки опций",
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  }, [isAdmins, message]);

  // Первичная инициализация страницы — только загрузка опций фильтров.
  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, []);

  // Реагируем на появление параметра search в URL уже после монтирования
  useEffect(() => {
    if (optionsPending) return;

    const parsed = parseParamsFromUrl();

    const updatedTableParams = {
      ...tableParams,
      ...parsed,
    };

    setTableParams(updatedTableParams);
    setSearchInput(parsed.filters.search || "");

    if (parsed.filters.search) {
      fetchData(updatedTableParams);
    } else {
      setData(null);
      setTotal(0);
    }

    // eslint-disable-next-line
  }, [searchParams, optionsPending]);

  // Общий helper для изменения фильтров.
  // shouldFetch позволяет управлять, нужно ли сразу делать запрос:
  // - true: фильтр применяется и сразу тянем данные,
  // - false: только обновляем состояние (например, для промежуточного ввода).
  const updateFilters = useCallback(
    (localParams, newFilters, shouldFetch = true, resetPage = true) => {
      const updatedFilters = { ...localParams.filters, ...newFilters };

      const updatedTableParams = {
        ...localParams,
        pagination: {
          ...localParams.pagination,
          current: resetPage ? 1 : localParams.pagination.current,
        },
        filters: updatedFilters,
      };

      setTableParams(updatedTableParams);

      //синхронизация с URL
      setSearchParams(serializeParamsToUrl(updatedTableParams));

      if (shouldFetch) {
        fetchData(updatedTableParams);
      }

      return updatedTableParams;
    },
    [fetchData, serializeParamsToUrl, setSearchParams],
  );

  // Обработчик изменений фильтров.
  // Дополнительные правила:
  // - при ручном выборе units_uuids сбрасываем access_level в "all";
  // - при смене access_level автоматически пересчитываем units_uuids.
  const handleFilterChange = useCallback(
    (name, val, shouldFetch = true) => {
      updateFilters(tableParams, buildFilterPatch(name, val), shouldFetch);
    },
    [tableParams, updateFilters, buildFilterPatch],
  );

  // Смена сортировки всегда сразу перезапрашивает данные.
  const handleSortChange = useCallback(
    (val) => {
      const updatedTableParams = {
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          current: 1,
        },
        sort: val,
      };
      setTableParams(updatedTableParams);
      fetchData(updatedTableParams);
    },
    [tableParams, fetchData],
  );

  const handlePaginationChange = useCallback(
    (current, pageSize) => {
      // При изменении pageSize сбрасываем страницу на 1
      const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

      const updatedTableParams = {
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          current: shouldResetPage ? 1 : current,
          pageSize,
        },
      };
      setTableParams(updatedTableParams);
      fetchData(updatedTableParams);
      setSearchParams(serializeParamsToUrl(updatedTableParams));
    },
    [tableParams, fetchData, serializeParamsToUrl, setSearchParams],
  );

  // Применяет поиск: переносит значение из input в filters.search и запускает запрос.
  const applySearch = useCallback(
    (rawSearch) => {
      const normalizedSearch = rawSearch?.trim() || null;

      const updatedTableParams = {
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          current: 1,
        },
        filters: {
          ...tableParams.filters,
          search: normalizedSearch,
        },
      };

      setTableParams(updatedTableParams);

      if (normalizedSearch) {
        fetchData(updatedTableParams);
        setTableParams(updatedTableParams);
        setSearchParams(serializeParamsToUrl(updatedTableParams));
      } else {
        setData(null);
        setTotal(0);
      }
    },
    [tableParams, fetchData, serializeParamsToUrl, setSearchParams],
  );

  // Во время ввода обновляем только локальное поле, запросы не запускаем
  const handleSearchChange = useCallback((event) => {
    setSearchInput(event.target.value);
  }, []);

  const handleToggleAllSections = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      handleFilterChange(
        FILTER_NAMES.SECTIONS,
        isAllSectionsSelected ? [] : ALL_SECTION_VALUES,
      );
    },
    [handleFilterChange, isAllSectionsSelected],
  );

  const openFiltersModal = useCallback(() => {
    setTempFilters({ ...tableParams.filters });
    setIsFiltersModalShow(true);
  }, [tableParams.filters]);

  const handleTempFilterChange = useCallback(
    (name, val) => {
      setTempFilters((prev) => ({
        ...prev,
        ...buildFilterPatch(name, val),
      }));
    },
    [buildFilterPatch],
  );

  const handleApplyModalFilters = useCallback(() => {
    updateFilters(tableParams, { ...tempFilters });
    setIsFiltersModalShow(false);
  }, [tableParams, tempFilters, updateFilters]);

  const handleResetModalFilters = useCallback(() => {
    const resetFilters = {
      ...getInitialFilters(),
      // Поиск из инпута оставляем, чтобы выполнить запрос после сброса
      // именно фильтров модалки.
      search: tableParams.filters.search,
    };

    updateFilters(tableParams, resetFilters);
    setIsFiltersModalShow(false);
  }, [tableParams, updateFilters]);

  const handleCancelModalFilters = () => {
    setIsFiltersModalShow(false);
  };

  const handleToggleAllTempSections = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      handleTempFilterChange(
        FILTER_NAMES.SECTIONS,
        isAllTempSectionsSelected ? [] : ALL_SECTION_VALUES,
      );
    },
    [handleTempFilterChange, isAllTempSectionsSelected],
  );

  //Управление модальным окном просмотра файла
  const handleOpenFileModal = (file) => {
    setSelectedFile(file);
    setIsFileModalOpen(true);
  };

  const handleCloseFileModal = () => {
    setIsFileModalOpen(false);
    setSelectedFile(null);
  };

  const handleFileDownload = () => {
    if (!selectedFile?.uuid) return;

    const link = document.createElement("a");
    link.href = `/api/file/download?uuid=${selectedFile.uuid}`;
    link.download = selectedFile.original_name;
    link.click();
  };

  const handleOpenFolder = async () => {
    if (!selectedFile?.uuid) return;

    try {
      const file = await getFile(selectedFile.uuid, isAdmins);

      const folderUuid = file?.folder_uuid || "catalog";

      const folderPath = isAdmins
        ? routes.administration.folder(folderUuid)
        : routes.folder(folderUuid);

      handleCloseFileModal();

      const params = new URLSearchParams();
      params.set("file_uuid", selectedFile.uuid);

      navigate({
        pathname: folderPath,
        search: `?${params.toString()}`,
      });
    } catch (e) {
      message.open({
        type: "error",
        content: e?.message || "Не удалось открыть папку",
      });
    }
  };

  return {
    // options
    optionsPending,
    units,
    unitsTree,
    // data
    data,
    total,
    pending,
    // table params
    tableParams,
    // search input
    searchInput,
    // modal
    isFiltersModalShow,
    tempFilters,
    isFileModalOpen,
    selectedFile,
    // derived
    selectedSections,
    tempSelectedSections,
    isAllSectionsSelected,
    isAllTempSectionsSelected,
    // handlers
    applySearch,
    handleSearchChange,
    handleFilterChange,
    handleSortChange,
    handlePaginationChange,
    handleToggleAllSections,
    openFiltersModal,
    handleTempFilterChange,
    handleApplyModalFilters,
    handleResetModalFilters,
    handleCancelModalFilters,
    handleToggleAllTempSections,
    setIsFileModalOpen,
    handleOpenFileModal,
    handleCloseFileModal,
    handleFileDownload,
    handleOpenFolder,
  };
};
